import React from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useTakeOverDemo } from 'hooks/api'
import { DemoListItem } from 'services/demosApi'

import { Typography } from '@mui/material'

import { Alert } from '@repo/ui'

import { ConfirmSettingModal } from '../ConfirmSettingModal'

interface TakeOverDemoModalProps {
  demo: DemoListItem | null
  open: boolean
  onClose: () => void
}

/**
 * VRG-361 Slice 2 — the confirmation gate on converting a demo into a real,
 * billable customer account.
 *
 * ## Why `ConfirmSettingModal` and not a bespoke modal
 *
 * §43's test is "is the effect downstream or hard to reverse?", and this is both,
 * at the extreme: money starts moving and `demo_taken_over_at` is stamped exactly
 * once, so there is no second attempt to correct a mistaken first one. What that
 * modal contributes is the thing a plain confirm cannot — a REQUIRED `consequence`
 * that has to say, in the user's own terms, what the click costs. "Are you sure?"
 * would be strictly worse than no gate, because it implies the reader has been
 * told something.
 *
 * ## The three facts the copy has to carry, and why these three
 *
 * 1. **AE access ends.** The seeded `vera_support` grant row is deleted here
 *    (VRG-369). This is the one effect the person clicking is likely to be
 *    surprised by, because it takes away THEIR own access, mid-relationship.
 * 2. **Billing starts.** `is_example` goes false, so the brand's real certificates
 *    begin metering. Forward-only: there is no retroactive charge for the demo
 *    period, and their demo certificates never meter at all (`example_kind` keeps
 *    them off it), which is why the copy can promise the demo data is safe to
 *    leave in place.
 * 3. **It cannot be undone.**
 *
 * The subdomain release is deliberately NOT in the consequence copy. It is a
 * benefit rather than a cost, it needs no decision now, and a warning Alert that
 * mixes costs with benefits reads as neither. It is reported in the success
 * toast, where it is actionable.
 *
 * ## No em-dashes in the body copy (§15) and no "purge" framing
 *
 * The demo products and certificates are deliberately left in place — the brand
 * clears them at leisure, which is safe because every one of them is badged. The
 * copy says so explicitly, because an AE who assumes conversion cleans up would
 * otherwise tell the brand something untrue on the same call.
 */
export const TakeOverDemoModal = ({ demo, open, onClose }: TakeOverDemoModalProps) => {
  const { open: notify } = useSnackbar()
  const { mutate: takeOver, isPending } = useTakeOverDemo()
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  const handleClose = () => {
    setSubmitError(null)
    onClose()
  }

  const handleConfirm = () => {
    if (!demo) return
    setSubmitError(null)
    takeOver(
      { companyId: demo.companyId },
      {
        onSuccess: (result) => {
          // A toast rather than an inline success state, because the row itself
          // changes visibly (the takeover date appears and the demo actions go
          // away). What the row CANNOT show is the follow-up: which name is now
          // free, and that the demo certificates are still sitting there. That is
          // what the text carries.
          const claimable =
            result.claimableSubdomain && result.claimableSubdomainAvailable
              ? ` They can now claim ${result.claimableSubdomain} in Settings.`
              : ''
          const retained = result.demoCertificatesRetained
            ? ` ${result.demoCertificatesRetained} demo certificate${
                result.demoCertificatesRetained === 1 ? '' : 's'
              } stayed in place, badged as demo data.`
            : ''
          notify({
            type: 'success',
            title: `${result.companyName} is now a real account`,
            text: `Vera support access has been removed and billing has started.${claimable}${retained}`,
          })
          handleClose()
        },
        onError: (error) => {
          // Inline, not a toast. The API's refusals each name a different missing
          // precondition (not a demo, already taken over, nobody has signed in yet)
          // and the AE's next move differs for each, so the message has to stay on
          // screen next to the button that produced it rather than fading.
          setSubmitError(getErrorMessage(error))
        },
      }
    )
  }

  return (
    <ConfirmSettingModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title="Convert this demo to a real account?"
      confirmLabel="Convert to real account"
      loading={isPending}
      consequence={
        <>
          <strong>{demo?.companyName ?? 'This brand'}</strong> becomes a real customer
          account. Vera staff lose the support access used to build this demo, and the
          brand starts being billed for the certificates they create from now on. Their
          demo products and certificates stay in place, badged as demo data, for them to
          clear whenever they want. This cannot be undone.
        </>
      }
    >
      {submitError && (
        <Alert severity="error" description={submitError} />
      )}
      {demo && !submitError && (
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {demo.joinedUserCount === 1
            ? '1 person from the brand has signed in.'
            : `${demo.joinedUserCount} people from the brand have signed in.`}
        </Typography>
      )}
    </ConfirmSettingModal>
  )
}
